<!-- Responsibility: Implement journal panel behavior for the ui/panels surface.
Scope: Owns ui/panels concerns only; cross-surface orchestration is excluded. -->
<journal-panel>
  <div class="panel-content">
    <h3>Journal</h3>
    <div if={ state.hiddenPotentialCount > 0 } class="journal-reveal-note">
      { state.hiddenPotentialCount } potential quest{ state.hiddenPotentialCount === 1 ? '' : 's' } hidden for now. Keep exploring to surface more.
    </div>

    <div if={ !state.quests.length } class="journal-empty">No recorded quests or goals yet.</div>

    <div each={ group in state.groups } class="quest-group" if={ group.quests.length }>
      <div class="quest-group-label">{ group.label }</div>
      <div
        each={ q in group.quests }
        class="quest-card status-{ q.status }{ state.recentlyChanged.has(q.id) ? ' quest-flash' : '' }"
        onclick={ () => openQuest(q) }
      >
        <div class="quest-title-row">
          <span class="quest-icon">{ questIcon(q) }</span>
          <span class="quest-title">{ q.title }</span>
          <span class="quest-status">{ q.status }</span>
        </div>
        <p class="quest-desc" if={ q.desc }>{ q.desc }</p>
        <div class="quest-deps" if={ q.dependsOn?.length }>
          Requires: { (q.dependsOn ?? []).join(', ') }
        </div>
      </div>
    </div>

  </div>

  <script>
    import { dispatcher } from '../../app-states.js'
    import { buildInfoDialog } from '../dialog/dialog-openers.ts'
    import { TOPICS } from "@anyquest/shared-core"
    import * as gameStore from '../../game-store.js'
    import { applyQuestVisibility } from '../helpers/quest-visibility.js'

    const TYPE_ORDER = ['quest', 'mini-quest', 'want', 'wish']
    const TYPE_LABELS = { quest: 'QUESTS', 'mini-quest': 'TASKS', want: 'WANTS', wish: 'WISHES' }
    const ICONS = { quest: '⚔', 'mini-quest': '◈', want: '◆', wish: '✧' }
    const STATUS_ORDER = ['active', 'potential', 'dormant', 'completed', 'failed']

    export default {
      state: {
        quests: [],
        groups: [],
        recentlyChanged: new Set(),
        hiddenPotentialCount: 0,
      },

      onMounted() {
        this.flashTimer = null
        this.refresh()
        this.handles = [
          dispatcher.on(TOPICS.game.loaded,           () => { this.refresh() }),
          dispatcher.on(TOPICS.game.started,          () => { this.refresh() }),
          dispatcher.on(TOPICS.game.actionComplete,   () => { this.refresh() }),
          dispatcher.on(TOPICS.world.ticked,          () => { this.refresh() }),
          dispatcher.on(TOPICS.world.questUpdated,    (_e, data) => { this.flashQuest(data.id) }),
        ]
      },

      onUnmounted() {
        this.handles?.forEach(h => dispatcher.off(h))
        if (this.flashTimer !== null) clearTimeout(this.flashTimer)
      },

      refresh() {
        const quests = gameStore.state?.journal ?? []
        const historyCount = (gameStore.state?.history ?? []).length
        const { visible, hiddenPotentialCount } = applyQuestVisibility(quests, historyCount)
        const groups = TYPE_ORDER.map(type => ({
          label: TYPE_LABELS[type],
          quests: visible
            .filter(q => q.type === type)
            .sort((a, b) => STATUS_ORDER.indexOf(a.status) - STATUS_ORDER.indexOf(b.status)),
        }))
        this.update({ quests: visible, groups, hiddenPotentialCount })
      },

      flashQuest(id) {
        if (this.flashTimer !== null) clearTimeout(this.flashTimer)
        this.update({ recentlyChanged: new Set([id]) })
        this.flashTimer = setTimeout(() => {
          this.flashTimer = null
          this.update({ recentlyChanged: new Set() })
        }, 2000)
      },

      questIcon(q) { return ICONS[q.type] ?? '·' },

      openQuest(q) {
        dispatcher.trigger('journal-panel', TOPICS.ui.dialogOpen, buildInfoDialog({
          options: {
            title: q.title,
            desc: q.desc,
            meta: `${q.type.toUpperCase()} · ${q.status.toUpperCase()}`,
          },
        }))
      },
    }
  </script>

  <style>
    .journal-reveal-note {
      margin: 0 0 0.65rem;
      padding: var(--space-xs) var(--space-xs);
      border: 1px dashed color-mix(in srgb, var(--gold) var(--alpha-mid), transparent);
      border-radius: 0.3125rem;
      background: color-mix(in srgb, var(--gold) var(--alpha-glow), transparent);
      color: var(--text-muted);
      font-size: 0.64rem;
      letter-spacing: 0.04em;
      line-height: 1.35;
    }

    .quest-flash {
      animation: questFlash 0.5s ease-in-out 3;
    }
  </style>
</journal-panel>
